// src/pages/HomePage/sections/StatsSection.jsx
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Stack,
  LinearProgress,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import ScienceIcon from "@mui/icons-material/Science";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import MemoryIcon from "@mui/icons-material/Memory";
import BoltIcon from "@mui/icons-material/Bolt";
import StorageIcon from "@mui/icons-material/Storage";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import InsightsIcon from "@mui/icons-material/Insights";
import { GradientTitle } from "../components/GradientTitle";
import { IconLight } from "../components/IconNode";

// === Hooks (kept consistent with the existing data layer) ===
import { useUserCount } from "../../../hooks/useUserCount";
import { useDatasets } from "../../../hooks/useDatasets";
import { useCachedStats } from "../../../hooks/useCachedStats";
import { useCachedWorkflowStats } from "../../../hooks/useCachedWorkflowStats";

const ACCENT = "#3F9A82";

const numberOrDash = (n, digits = 1) =>
  Number.isFinite(n) ? Number(n).toFixed(digits) : "...";

const compact = (n) =>
  Number.isFinite(n)
    ? Intl.NumberFormat("en", { notation: "compact" }).format(n)
    : "...";

const datasetUsageCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) && count >= 0 ? count : 0;
};

/* --- A single stat row in the platform column --- */
const StatRow = ({ icon: Icon, label, value, loading }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1.25 }}>
    <IconLight icon={Icon} size={38} />
    <Box>
      <Typography
        sx={{ fontWeight: 800, fontSize: "1.3rem", lineHeight: 1.05 }}
      >
        {loading ? "..." : value}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
    </Box>
  </Stack>
);

/* --- Column header: an eyebrow-style section title with a divider, so it
   reads as a heading rather than another icon + text content row. --- */
const ColumnHeader = ({ icon: Icon, title, subtitle }) => (
  <Box
    sx={{
      mb: 2.5,
      pb: 1.5,
      borderBottom: "1px solid",
      borderColor: "rgba(0,0,0,0.08)",
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      {Icon && <Icon sx={{ fontSize: 20, color: ACCENT }} />}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.78rem",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "#1F5E50",
        }}
      >
        {title}
      </Typography>
    </Stack>
    {subtitle && (
      <Typography
        variant="body2"
        sx={{ mt: 0.75, color: "text.secondary", fontSize: "0.82rem" }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

/* --- A ranked row in a leaderboard --- */
const LeaderboardRow = ({ rank, name, value, max, unit }) => {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
      <Box
        sx={{
          width: 24,
          flexShrink: 0,
          textAlign: "center",
          fontWeight: 800,
          fontSize: "0.95rem",
          color: rank <= 3 ? ACCENT : "text.secondary",
        }}
      >
        {rank}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          direction="row"
          alignItems="baseline"
          spacing={1}
          sx={{ mb: 0.5 }}
        >
          <Typography
            noWrap
            sx={{ fontWeight: 600, fontSize: "0.9rem", flex: 1, minWidth: 0 }}
          >
            {name}
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
            {compact(value)} {unit}
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: "rgba(0,0,0,0.06)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 3,
              backgroundColor: ACCENT,
            },
          }}
        />
      </Box>
    </Stack>
  );
};

/* --- Big, faded icon watermark sitting behind a card's content --- */
const CardWatermark = ({ icon: Icon }) => (
  <Box
    aria-hidden
    sx={{
      position: "absolute",
      right: -30,
      bottom: -36,
      lineHeight: 0,
      pointerEvents: "none",
      zIndex: 0,
    }}
  >
    <Icon sx={{ fontSize: 260, color: ACCENT, opacity: 0.06 }} />
  </Box>
);

/* --- A leaderboard card --- */
const Leaderboard = ({ icon, title, subtitle, items, unit, message }) => {
  const max = Math.max(1, ...items.map((i) => i.value || 0));
  return (
    <Card
      variant="whiteCardColorfulShadow"
      sx={{ height: "100%", position: "relative", overflow: "hidden" }}
    >
      <CardWatermark icon={icon} />
      <CardContent
        sx={{ p: { xs: 3, md: 4 }, position: "relative", zIndex: 1 }}
      >
        <ColumnHeader icon={icon} title={title} subtitle={subtitle} />
        {message && (
          <Typography variant="body2" color="text.secondary" role="status">
            {message}
          </Typography>
        )}
        <Box>
          {items.map((it, i) => (
            <LeaderboardRow
              key={`${it.name}-${i}`}
              rank={i + 1}
              name={it.name}
              value={it.value}
              max={max}
              unit={unit}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

Leaderboard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string, value: PropTypes.number }),
  ).isRequired,
  unit: PropTypes.string.isRequired,
  message: PropTypes.string,
};

/* --- Main component --- */
const StatsSection = () => {
  const { userCount, loading: userCountLoading } = useUserCount();
  const { datasets, datasetCount, loading: datasetLoading } = useDatasets();
  const { cachedStats, loading: cachedStatsLoading } = useCachedStats();
  const {
    workflows,
    loading: workflowLoading,
    error: workflowError,
  } = useCachedWorkflowStats();

  const workflowMessage = workflowLoading
    ? "Loading workflows..."
    : workflowError
      ? "Workflow usage is currently unavailable."
      : workflows.length === 0
        ? "No workflow images are available."
        : null;
  const workflowLeaders = workflows.map((workflow) => ({
    name: workflow.name,
    value: workflow.launchCount,
  }));

  const datasetLeaders = (datasets || [])
    .map((dataset) => ({
      id: dataset.id,
      name: dataset.title || "Untitled dataset",
      value: datasetUsageCount(dataset.recorded_usage_count),
    }))
    .sort(
      (a, b) =>
        b.value - a.value ||
        a.name.localeCompare(b.name, undefined, {
          sensitivity: "base",
          numeric: true,
        }) ||
        String(a.id).localeCompare(String(b.id)),
    )
    .slice(0, 7);

  const experimentCount = cachedStats?.experimentCount;
  const experimentCountPastMonth = cachedStats?.experimentCountPastMonth;
  const cpuHours = cachedStats?.cpuUtilizationPastMonth;
  const gpuHours = cachedStats?.gpuUtilizationPastMonth;
  const storageUsedTB = cachedStats?.storageUsage;

  const anyLoading = userCountLoading || datasetLoading || cachedStatsLoading;

  const [firstLoad, setFirstLoad] = useState(true);
  useEffect(() => {
    if (!anyLoading && firstLoad) setFirstLoad(false);
  }, [anyLoading, firstLoad]);

  const showLoading = firstLoad && anyLoading;

  return (
    <Box
      id="stats"
      sx={{
        position: "relative",
        overflow: "hidden",
        pt: { xs: 4, md: 6 },
        pb: { xs: 8, md: 12 },
        px: { xs: 2, md: 4 },
      }}
    >
      {/* Static light-green background (no animation) */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          pointerEvents: "none",
          background:
            "linear-gradient(180deg, #EEF6EF 0%, #E5F1E8 60%, #DEEDE3 100%)",
        }}
      />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <GradientTitle>We are serving</GradientTitle>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: { xs: 3, md: 4 },
            alignItems: "stretch",
          }}
        >
          {/* Column 1: platform statistics */}
          <Card
            variant="whiteCardColorfulShadow"
            sx={{ height: "100%", position: "relative", overflow: "hidden" }}
          >
            <CardWatermark icon={InsightsIcon} />
            <CardContent
              sx={{ p: { xs: 3, md: 4 }, position: "relative", zIndex: 1 }}
            >
              <ColumnHeader
                icon={InsightsIcon}
                title="Platform"
                subtitle="Live usage at a glance"
              />
              <StatRow
                icon={GroupIcon}
                label="Users"
                value={numberOrDash(userCount, 0)}
                loading={showLoading}
              />
              <StatRow
                icon={ScienceIcon}
                label="Experiments this month / now"
                value={`${numberOrDash(experimentCountPastMonth, 0)} / ${numberOrDash(experimentCount, 0)}`}
                loading={showLoading}
              />
              <StatRow
                icon={Inventory2Icon}
                label="Datasets"
                value={numberOrDash(datasetCount, 0)}
                loading={showLoading}
              />
              <StatRow
                icon={BoltIcon}
                label="GPU hours this month"
                value={numberOrDash(gpuHours, 1)}
                loading={showLoading}
              />
              <StatRow
                icon={MemoryIcon}
                label="CPU core hours this month"
                value={numberOrDash(cpuHours, 1)}
                loading={showLoading}
              />
              <StatRow
                icon={StorageIcon}
                label="TB storage used"
                value={numberOrDash(storageUsedTB, 1)}
                loading={showLoading}
              />
            </CardContent>
          </Card>

          {/* Column 2: workloads leaderboard */}
          <Leaderboard
            icon={LeaderboardIcon}
            title="Top Workflows"
            subtitle="By launches in the past 30 days"
            items={workflowLeaders}
            unit="uses"
            message={workflowMessage}
          />

          {/* Column 3: datasets leaderboard */}
          <Leaderboard
            icon={Inventory2Icon}
            title="Top Datasets"
            subtitle="By recorded usage"
            items={datasetLeaders}
            unit="uses"
          />
        </Box>
      </Container>
    </Box>
  );
};

export default StatsSection;
